import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Send, CheckCircle, ArrowLeft, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBJECTS = [
  'Modification de données généalogiques',
  "Ajout d'une personne ou d'une famille",
  "Correction d'un événement ou d'une date",
  "Correction d'un lieu",
  'Lien familial manquant ou incorrect',
  "Signalement d'une erreur",
  'Autre demande',
];

// ── Recherche de personne avec autocomplétion ─────────────────────────────────
const PersonSearch = ({ value, onChange }) => {
  const [searchTerm,  setSearchTerm]  = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg,    setShowSugg]    = useState(false);
  const ref = useRef(null);

  // Ferme les suggestions si clic en dehors
  useEffect(() => {
    const handler = (e) => { if (!ref.current?.contains(e.target)) setShowSugg(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Recherche avec debounce
  useEffect(() => {
    if (!searchTerm.trim() || searchTerm.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/persons`, { withCredentials: true });
        const q = searchTerm.toLowerCase();
        setSuggestions(
          data.filter(p =>
            `${p.given_name || ''} ${p.surname || ''}`.toLowerCase().includes(q)
          ).slice(0, 8)
        );
        setShowSugg(true);
      } catch { setSuggestions([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const select = (person) => {
    const name = `${person.given_name || ''} ${person.surname || ''}`.trim();
    const ref  = person.gramps_id ? `${name} (${person.gramps_id})` : name;
    setSearchTerm(ref);
    onChange(ref);
    setSuggestions([]);
    setShowSugg(false);
  };

  const clear = () => { setSearchTerm(''); onChange(''); setSuggestions([]); };

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2"
          style={{ color: 'var(--text-muted)' }} />
        <input
          type="text"
          value={searchTerm}
          onChange={e => { setSearchTerm(e.target.value); onChange(e.target.value); }}
          onFocus={() => suggestions.length && setShowSugg(true)}
          placeholder="Tapez un nom pour rechercher…"
          className="w-full pl-9 pr-8 py-2.5 text-sm border rounded-sm focus:outline-none"
          style={{ backgroundColor: 'var(--bg-input)', borderColor: 'var(--border)',
                   color: 'var(--text-primary)' }}
        />
        {searchTerm && (
          <button type="button" onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--text-muted)' }}>
            <X size={14} />
          </button>
        )}
      </div>

      {/* Suggestions */}
      {showSugg && suggestions.length > 0 && (
        <div className="absolute z-20 w-full mt-1 border shadow-lg max-h-56 overflow-y-auto"
          style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}>
          {suggestions.map(p => (
            <button key={p.handle} type="button" onClick={() => select(p)}
              className="w-full text-left px-4 py-2.5 border-b text-sm last:border-0"
              style={{ borderColor: 'var(--border)', color: 'var(--text-primary)' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              <span className="font-medium" style={{ color: 'var(--forest)' }}>
                {p.given_name} {p.surname}
              </span>
              {p.gramps_id && (
                <span className="text-xs ml-2" style={{ color: 'var(--text-muted)' }}>
                  {p.gramps_id}
                </span>
              )}
            </button>
          ))}
          <div className="px-4 py-2 text-xs italic border-t"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
            Vous pouvez aussi saisir un nom manuellement si la personne n'apparaît pas.
          </div>
        </div>
      )}
    </div>
  );
};

// ── Page de contact ───────────────────────────────────────────────────────────
export const ContactPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ subject: '', person_ref: '', message: '' });
  const [sent,    setSent]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const handleChange = (e) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handlePersonChange = (val) =>
    setForm(prev => ({ ...prev, person_ref: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.message.trim()) {
      setError('Veuillez remplir le sujet et le message.'); return;
    }
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Erreur serveur');
      }
      setSent(true);
    } catch (err) {
      setError(err.message || "Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) return (
    <div className="max-w-lg mx-auto">
      <div className="border p-10 text-center"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}>
        <CheckCircle size={48} className="mx-auto mb-4" style={{ color: 'var(--forest)' }} />
        <h2 className="text-2xl font-bold mb-2"
          style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
          Demande envoyée
        </h2>
        <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
          Votre demande a bien été transmise aux administrateurs.
          Elle sera traitée dans les meilleurs délais.
        </p>
        <button onClick={() => navigate(-1)}
          className="text-sm font-medium underline" style={{ color: 'var(--terracotta)' }}>
          Retour
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm font-medium"
        style={{ color: 'var(--terracotta)' }}>
        <ArrowLeft size={16} /> Retour
      </button>

      <div>
        <h1 className="text-4xl font-bold mb-1"
          style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
          Nous contacter
        </h1>
        <p style={{ color: 'var(--text-muted)' }}>
          Signalez une erreur ou demandez une modification complexe
        </p>
      </div>

      <div className="border p-6 md:p-8"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}>

        {user && (
          <div className="flex items-center gap-3 mb-6 pb-4 border-b text-sm"
            style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
            <Mail size={16} style={{ color: 'var(--forest)' }} />
            Envoi en tant que <strong>{user.prenom} {user.nom}</strong> ({user.email})
          </div>
        )}

        {error && (
          <div className="px-4 py-3 mb-4 text-sm border rounded-sm"
            style={{ color: 'var(--terracotta)', borderColor: 'var(--terracotta)',
                     backgroundColor: 'rgba(168,87,60,0.06)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Sujet */}
          <div>
            <label className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--text-secondary)' }}>Sujet *</label>
            <select name="subject" value={form.subject} onChange={handleChange} required
              className="w-full px-3 py-2.5 text-sm border rounded-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-input)', borderColor: 'var(--border)',
                       color: 'var(--text-primary)' }}>
              <option value="">-- Choisissez un sujet --</option>
              {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Personne concernée avec autocomplétion */}
          <div>
            <label className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--text-secondary)' }}>
              Personne concernée
              <span className="font-normal ml-1" style={{ color: 'var(--text-muted)' }}>
                (optionnel)
              </span>
            </label>
            <PersonSearch value={form.person_ref} onChange={handlePersonChange} />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--text-secondary)' }}>Message *</label>
            <textarea name="message" value={form.message} onChange={handleChange}
              required rows={6}
              placeholder="Décrivez précisément la modification souhaitée ou l'erreur constatée…"
              className="w-full px-3 py-2.5 text-sm border rounded-sm focus:outline-none resize-y"
              style={{ backgroundColor: 'var(--bg-input)', borderColor: 'var(--border)',
                       color: 'var(--text-primary)' }} />
          </div>

          <button type="submit" disabled={loading}
            className="w-full py-3 text-sm font-semibold text-white rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ backgroundColor: 'var(--forest)' }}>
            <Send size={16} />
            {loading ? 'Envoi…' : 'Envoyer la demande'}
          </button>
        </form>
      </div>
    </div>
  );
};
